import { lazy, Suspense, useEffect } from "react";

// Deep import — the dashboard barrel would pull react-grid-layout eagerly.
import { ErrorBoundary } from "dashboard/ErrorBoundary";
import DefaultContent from "pages/defaultContent/DefaultContent";
import { usePage } from "contexts/PageContext";
import { useAppUI } from "contexts/UIContext";
import Alert from "components/common/Alert";
import Sidebar from "../Sidebar";

const SeasonalityPage = lazy(() => import("pages/seasonality/SeasonalityPage"));
const EuEtsReport = lazy(() => import("pages/euEtsReport/EuEtsReport"));
const Token = lazy(() => import("pages/token/Token"));
const UpdatedVentura = lazy(() => import("pages/Ventura/UpdatedVentura"));
const FFAOverviewPage = lazy(() => import("pages/ffaOverview/FFAOverviewPage"));
const SeaMarginPage = lazy(() => import("pages/seaMargin/SeaMarginPage"));
const WeatherInPortsPage = lazy(
  () => import("pages/weatherInPorts/WeatherInPortsPage"),
);
const TeamEstimatesPage = lazy(
  () => import("pages/teamEstimates/TeamEstimatesPage"),
);
const VesselViewPage = lazy(() => import("pages/vesselView/VesselViewPage"));
const BusinessCardPage = lazy(
  () => import("pages/businessCard/BusinessCardPage"),
);
const DirectoryPage = lazy(() => import("pages/directory/DirectoryPage"));
const EmissionReportPage = lazy(
  () => import("pages/emissionReport/EmissionReportPage"),
);
const FileSharingPage = lazy(() => import("pages/fileSharing/FileSharingPage"));
const FdtrStrategiesPage = lazy(
  () => import("pages/fdtrStrategies/FdtrStrategiesPage"),
);
const TradesPage = lazy(() => import("pages/trades/TradesPage"));
const DataloyPage = lazy(() => import("pages/dataloy/DataloyPage"));
const CargoPlanPage = lazy(() => import("pages/cargoPlan/CargoPlanPage"));
const CargoListPage = lazy(() => import("pages/cargoList/CargoListPage"));
const SignalsPage = lazy(() => import("pages/signals/SignalsPage"));

const PageFallback = () => (
  <div className="d-flex justify-content-center align-items-center w-100 py-5">
    <div className="spinner-border" role="status">
      <span className="visually-hidden">Loading...</span>
    </div>
  </div>
);

function MainContent() {
  const { page } = usePage();
  const { setAppLoading, setAppModal, setAlert, sidebarOpen, alertProps } =
    useAppUI();

  const searchParams = new URLSearchParams(window.location.search);
  const parsedParams: { [key: string]: string } = {};
  searchParams.forEach((value, key) => {
    if (
      ["id_token", "access_token", "session_state", "code", "state"].includes(
        key,
      )
    ) {
      return;
    }
    parsedParams[key] = value;
  });

  useEffect(() => {
    /*
      Reset global UI components on first render
      to avoid blocking the screen
    */
    setAppLoading(false);
    setAppModal(null);
    setAlert(null);
  }, [page]);

  const renderContent = () => {
    switch (page) {
      case "ventura-port":
        return <UpdatedVentura estimateId={parsedParams["estimateId"]} />;
      case "eu-ets-report":
        return <EuEtsReport />;
      case "token":
        return <Token />;
      case "seasonality":
        return <SeasonalityPage />;
      case "ffa-overview":
        return <FFAOverviewPage />;
      case "sea-margin":
        return <SeaMarginPage />;
      case "emission-report":
        return <EmissionReportPage />;
      case "file-sharing":
        return <FileSharingPage />;
      case "weather-in-ports":
        return <WeatherInPortsPage />;
      case "team-estimates":
        return <TeamEstimatesPage />;
      case "vessel-view":
        return <VesselViewPage />;
      case "business-card":
        return <BusinessCardPage />;
      case "directory":
        return <DirectoryPage />;
      case "fdtr-strategies":
        return <FdtrStrategiesPage />;
      case "recap-manager":
        return <TradesPage />;
      case "dataloy":
        return <DataloyPage />;
      case "cargo-plan":
        return <CargoPlanPage />;
      case "cargo-orders":
        return <CargoListPage />;
      case "signals":
        return <SignalsPage />;
      default:
        return <DefaultContent />;
    }
  };

  return (
    <div className="root-page-wrapper container-fluid p-0 m-0">
      {/* Sidebar */}
      {sidebarOpen ? <Sidebar /> : null}

      <main
        className={`root-page${
          page === "seasonality" ||
          page === "ffa-overview" ||
          page === "sea-margin" ||
          page === "weather-in-ports" ||
          page === "team-estimates" ||
          page === "sea-margin" ||
          page === "emission-report" ||
          page === "file-sharing" ||
          page === "vessel-view" ||
          page === "directory" ||
          page === "fdtr-strategies" ||
          page === "recap-manager" ||
          page === "dataloy" ||
          page === "cargo-plan" ||
          page === "cargo-orders" ||
          page === "signals"
            ? " root-page--wide"
            : ""
        }`}
      >
        {/* Alert */}
        {alertProps ? <Alert {...alertProps} /> : null}

        {/* Page Content — keyed boundary resets on navigation; outside
            Suspense so failed lazy-chunk loads (post-deploy hash rotation)
            land on the retry card too. */}
        <ErrorBoundary key={page || "default"} label={page || "page"}>
          <Suspense fallback={<PageFallback />}>{renderContent()}</Suspense>
        </ErrorBoundary>
      </main>
    </div>
  );
}

export default MainContent;
